import { useState, useEffect } from 'react'

const KEYBOARD_THRESHOLD_PX = 150
const FOCUS_SETTLE_MS = 120

function isEditableElement(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  if (el.isContentEditable) return true
  if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true
  if (el.tagName !== 'INPUT') return false
  const type = (el as HTMLInputElement).type
  return ![
    'button', 'checkbox', 'color', 'file', 'hidden',
    'image', 'radio', 'range', 'reset', 'submit',
  ].includes(type)
}

function hasVirtualKeyboardDevice(): boolean {
  return window.matchMedia?.('(pointer: coarse)').matches ?? false
}

function viewportOccludedByKeyboard(): boolean {
  const vv = window.visualViewport
  if (!vv) return false
  return window.innerHeight - vv.height - vv.offsetTop > KEYBOARD_THRESHOLD_PX
}

/**
 * Detects when the on-screen keyboard is covering the Mini App.
 *
 * Telegram iOS often never restores `visualViewport.height` after the keyboard
 * closes, so viewport-only detection can leave the bottom nav hidden until reload.
 * Blurring an editable field is treated as keyboard-closed regardless of viewport.
 */
export function useKeyboardVisible(): boolean {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)

  useEffect(() => {
    let settleTimer: ReturnType<typeof setTimeout> | null = null

    const clearSettleTimer = () => {
      if (settleTimer) {
        clearTimeout(settleTimer)
        settleTimer = null
      }
    }

    const sync = () => {
      const editableFocused = isEditableElement(document.activeElement)

      // Keyboard cannot remain up without a focused field. This is the restore
      // path when Visual Viewport / Telegram never reports the keyboard closing.
      if (!editableFocused) {
        setIsKeyboardVisible(false)
        return
      }

      setIsKeyboardVisible(hasVirtualKeyboardDevice() || viewportOccludedByKeyboard())
    }

    const onFocusIn = () => {
      clearSettleTimer()
      sync()
    }

    const onFocusOut = () => {
      clearSettleTimer()
      settleTimer = setTimeout(sync, FOCUS_SETTLE_MS)
    }

    const vv = window.visualViewport
    if (vv) {
      vv.addEventListener('resize', sync)
      vv.addEventListener('scroll', sync)
    } else {
      window.addEventListener('resize', sync)
    }

    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)

    return () => {
      clearSettleTimer()
      if (vv) {
        vv.removeEventListener('resize', sync)
        vv.removeEventListener('scroll', sync)
      } else {
        window.removeEventListener('resize', sync)
      }
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])

  return isKeyboardVisible
}
